import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Siren, Edit, Trash2, Plus, X, AlertTriangle, ChevronLeft, ChevronRight, Clock, CalendarClock, Flame, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config';

const PriorityList = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ 1. INITIALIZE STATE FROM SESSION STORAGE
  // We use unique keys ('priority_search', etc.)
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem('priority_search') || '';
  });

  const [entriesPerPage, setEntriesPerPage] = useState(() => {
    return parseInt(sessionStorage.getItem('priority_entries')) || 10;
  });

  const [currentPage, setCurrentPage] = useState(() => {
    return parseInt(sessionStorage.getItem('priority_page')) || 1;
  });

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Edit/Delete States
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [patientToDelete, setPatientToDelete] = useState(null);

  // Form Data
  const [formData, setFormData] = useState({
    full_name: '', phone_number: '', dob: '', address: '',
    insurance_provider: '', insurance_policy_no: '', status: 'F',
    is_priority: false
  });

  // API_URL is now managed in src/config.js

  // ✅ 2. SAVE STATE WHENEVER IT CHANGES
  useEffect(() => {
    sessionStorage.setItem('priority_search', searchTerm);
    sessionStorage.setItem('priority_entries', entriesPerPage);
    sessionStorage.setItem('priority_page', currentPage);
  }, [searchTerm, entriesPerPage, currentPage]);

  // Auto-Scroll Logic
  useEffect(() => {
    if (!loading && patients.length > 0) {
      const lastViewedId = sessionStorage.getItem('lastViewedPriorityId');
      if (lastViewedId) {
        setTimeout(() => {
          const element = document.getElementById(`priority-row-${lastViewedId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('bg-teal-50');
            setTimeout(() => element.classList.remove('bg-teal-50'), 2000);
          }
        }, 100);
        sessionStorage.removeItem('lastViewedPriorityId');
      }
    }
  }, [loading, patients]);

  const fetchPriorityPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}?action=get_priority_patients`);
      if (Array.isArray(response.data)) setPatients(response.data);
      setLoading(false);
    } catch (error) { console.error(error); setLoading(false); }
  }, []);

  useEffect(() => {
    fetchPriorityPatients();
  }, [fetchPriorityPatients]);

  const computeUrgency = (patient) => {
    let score = 0;
    let reasons = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const daysUntilExpiry = parseInt(patient.days_until_expiry);
    if (!isNaN(daysUntilExpiry)) {
      if (daysUntilExpiry <= 0) {
        score += 50;
        reasons.push('Expired');
      } else if (daysUntilExpiry <= 7) {
        score += 40;
        reasons.push(`Expires in ${daysUntilExpiry}d`);
      } else if (daysUntilExpiry <= 14) {
        score += 30;
        reasons.push(`Expires in ${daysUntilExpiry}d`);
      } else if (daysUntilExpiry <= 30) {
        score += 15;
        reasons.push(`Expires in ${daysUntilExpiry}d`);
      }
    }

    const daysSinceFill = parseInt(patient.days_since_fill);
    const daysSupply = parseInt(patient.min_days_supply);
    if (!isNaN(daysSinceFill) && !isNaN(daysSupply) && daysSupply > 0) {
      const supplyRemaining = daysSupply - daysSinceFill;
      if (supplyRemaining <= 0) {
        score += 50;
        reasons.push('Supply ran out');
      } else if (supplyRemaining <= 3) {
        score += 40;
        reasons.push(`${supplyRemaining}d supply left`);
      } else if (supplyRemaining <= 7) {
        score += 30;
        reasons.push(`${supplyRemaining}d supply left`);
      } else if (supplyRemaining <= 14) {
        score += 15;
        reasons.push(`${supplyRemaining}d supply left`);
      }
    }

    // Manual high priority flag always gets boosted
    if (patient.is_priority === 1) {
      score += 10;
    }

    return { score, reasons };
  };

  // Determine urgency level label from score
  const getUrgencyLevel = (score) => {
    if (score >= 80) return 'CRITICAL';
    if (score >= 50) return 'HIGH';
    if (score >= 30) return 'MEDIUM';
    if (score >= 15) return 'LOW';
    return null;
  };

  const filteredPatients = patients.filter((p) => {
    const search = searchTerm.toLowerCase();
    return (p.full_name || '').toLowerCase().includes(search) || String(p.id).includes(search);
  }).map(p => {
    const urgency = computeUrgency(p);
    return { ...p, urgencyScore: urgency.score, urgencyReasons: urgency.reasons, urgencyLevel: getUrgencyLevel(urgency.score) };
  }).sort((a, b) => {
    // Sort: highest urgency first, then manual priority, then alphabetical
    if (b.urgencyScore !== a.urgencyScore) return b.urgencyScore - a.urgencyScore;
    if (b.is_priority !== a.is_priority) return (b.is_priority || 0) - (a.is_priority || 0);
    return (a.full_name || '').localeCompare(b.full_name || '');
  });

  // ✅ Pagination Logic
  const totalEntries = filteredPatients.length;
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(totalEntries / entriesPerPage);

  const startIndex = entriesPerPage === 'all' ? 0 : (currentPage - 1) * entriesPerPage;
  const endIndex = entriesPerPage === 'all' ? totalEntries : startIndex + entriesPerPage;
  const paginatedPatients = filteredPatients.slice(startIndex, endIndex);

  // ✅ 3. RESET PAGE LOGIC
  // If current page is greater than total pages (e.g., after filtering), reset to 1
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [searchTerm, totalPages, currentPage]);

  // --- HANDLERS ---

  // ✅ NEW: Handle Search Change (Resets Page)
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleViewDetails = (patientId) => {
    sessionStorage.setItem('lastViewedPriorityId', patientId);
    navigate(`/priority/${patientId}`);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Open Add Modal
  const openAddModal = () => {
    setIsEditing(false);
    setEditId(null);
    setFormData({
      full_name: '', phone_number: '', dob: '', address: '',
      insurance_provider: '', insurance_policy_no: '', status: 'F',
      is_priority: false
    });
    setShowModal(true);
  };

  // Open Edit Modal
  const handleEditClick = (e, patient) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditId(patient.id);
    setFormData({
      full_name: patient.full_name,
      phone_number: patient.phone_number,
      dob: patient.dob,
      address: patient.address || '',
      insurance_provider: patient.insurance_provider,
      insurance_policy_no: patient.insurance_policy_no || '',
      status: patient.status || 'Active',
      is_priority: patient.is_priority === 1
    });
    setShowModal(true);
  };

  // Open Delete Modal
  const confirmDeleteClick = (e, patient) => {
    e.stopPropagation();
    setPatientToDelete(patient);
    setShowDeleteModal(true);
  };

  // Execute Delete
  const executeDelete = async () => {
    if (!patientToDelete) return;
    try {
      await axios.post(`${API_URL}?action=delete_patient`, { id: patientToDelete.id });
      setShowDeleteModal(false);
      fetchPriorityPatients();
    } catch (error) { console.error(error); alert("Error deleting"); }
  };

  // Submit Form (Add/Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const action = isEditing ? 'update_patient' : 'add_patient';
    const payload = isEditing ? { ...formData, id: editId } : formData;

    try {
      await axios.post(`${API_URL}?action=${action}`, payload);
      setShowModal(false);
      fetchPriorityPatients();
    } catch (error) { console.error(error); alert("Error saving"); }
  };

  const formatID = (id) => String(id).padStart(4, '0');
  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '-';
    const cleanDate = dateString.split(' ')[0].split('T')[0];
    const [year, month, day] = cleanDate.split('-');
    if (year && month && day) return `${month}/${day}/${year}`;
    return dateString;
  };

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-700 flex items-center gap-3">
          <Siren className="text-teal-600" size={30} /> Priority Call List
        </h2>

        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search Patient..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-teal-500"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <button onClick={openAddModal} className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 flex items-center gap-2 shadow">
            <Plus size={16} /> Register
          </button>
        </div>
      </div>

      {/* ✅ Entries Dropdown */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show</span>
          <select
            value={entriesPerPage}
            onChange={(e) => {
              setEntriesPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
              setCurrentPage(1); // Reset page when changing entries
            }}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>

        <div className="text-sm text-gray-600">
          Showing {startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries
        </div>
      </div>

      {/* MAIN TABLE */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase">ID</th>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase">Patients Name</th>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase">Phone Number</th>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase">Insurance</th>
              <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase">RX Count</th>
              <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase">Last Fill</th>
              <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase">Expiry</th>
              <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase">Urgency</th>
              <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="10" className="text-center py-4">Loading...</td></tr>
            ) : paginatedPatients.length === 0 ? (
              <tr><td colSpan="10" className="text-center py-10 text-gray-400 italic">No priority patients (F, B, U) found.</td></tr>
            ) : (
              paginatedPatients.map((p) => {
                const isHighPriority = p.is_priority === 1;
                const urgencyLevel = p.urgencyLevel;

                const rowClass = urgencyLevel === 'CRITICAL'
                  ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-600'
                  : urgencyLevel === 'HIGH'
                    ? 'bg-orange-50 hover:bg-orange-100 border-l-4 border-l-orange-500'
                    : urgencyLevel === 'MEDIUM'
                      ? 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-l-yellow-500'
                      : isHighPriority
                        ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500'
                        : 'hover:bg-teal-50';

                return (
                  <tr
                    key={p.id}
                    id={`priority-row-${p.id}`}
                    className={`transition cursor-pointer ${rowClass}`}
                    onClick={() => handleViewDetails(p.id)}
                  >
                    <td className="px-4 py-3 font-mono text-gray-500">
                      <div className="flex items-center gap-2">
                        {(urgencyLevel === 'CRITICAL' || urgencyLevel === 'HIGH') && <Flame size={14} className="text-red-500 animate-pulse" />}
                        {isHighPriority && !urgencyLevel && <Siren size={14} className="text-red-500 animate-pulse" />}
                        {formatID(p.id)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-blue-600 font-bold hover:underline">{p.full_name}</span>
                        {isHighPriority && <span className="text-[10px] font-black text-red-600 uppercase tracking-widest">High Priority</span>}
                        {p.urgencyReasons && p.urgencyReasons.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {p.urgencyReasons.map((reason, idx) => (
                              <span key={idx} className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${reason.includes('ran out') || reason.includes('Expired')
                                ? 'bg-red-100 text-red-700'
                                : reason.includes('1d') || reason.includes('2d') || reason.includes('3d')
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                {reason}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.phone_number}</td>
                    <td className="px-4 py-3 text-gray-700">{p.insurance_provider}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-bold">
                        {p.rx_count} Active
                      </span>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center">
                        <span className="text-xs text-gray-600 flex items-center gap-1">
                          <Clock size={12} className="text-gray-400" />
                          {formatDate(p.latest_fill)}
                        </span>
                        {p.days_since_fill != null && p.min_days_supply != null && (
                          <span className={`text-[10px] font-bold mt-0.5 ${(p.min_days_supply - p.days_since_fill) <= 0 ? 'text-red-600' :
                            (p.min_days_supply - p.days_since_fill) <= 7 ? 'text-orange-600' :
                              'text-gray-400'
                            }`}>
                            {(p.min_days_supply - p.days_since_fill) <= 0
                              ? 'Supply exhausted'
                              : `${p.min_days_supply - p.days_since_fill}d remaining`}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Expiry Date */}
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center">
                        <span className={`text-xs flex items-center gap-1 ${p.days_until_expiry != null && p.days_until_expiry <= 7 ? 'text-red-600 font-bold' :
                          p.days_until_expiry != null && p.days_until_expiry <= 30 ? 'text-orange-600 font-semibold' :
                            'text-gray-600'
                          }`}>
                          <CalendarClock size={12} className={
                            p.days_until_expiry != null && p.days_until_expiry <= 7 ? 'text-red-500' : 'text-gray-400'
                          } />
                          {formatDate(p.nearest_expiry)}
                        </span>
                        {p.days_until_expiry != null && (
                          <span className={`text-[10px] font-bold mt-0.5 ${p.days_until_expiry <= 0 ? 'text-red-600' :
                            p.days_until_expiry <= 7 ? 'text-red-500' :
                              p.days_until_expiry <= 30 ? 'text-orange-500' :
                                'text-gray-400'
                            }`}>
                            {p.days_until_expiry <= 0 ? '⚠ EXPIRED' : `${p.days_until_expiry}d left`}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Urgency Badge */}
                    <td className="px-4 py-3 text-center">
                      {urgencyLevel ? (
                        <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider border inline-flex items-center gap-1 ${urgencyLevel === 'CRITICAL' ? 'bg-red-600 text-white border-red-700 animate-pulse shadow-sm' :
                          urgencyLevel === 'HIGH' ? 'bg-orange-500 text-white border-orange-600 shadow-sm' :
                            urgencyLevel === 'MEDIUM' ? 'bg-yellow-400 text-yellow-900 border-yellow-500' :
                              'bg-blue-100 text-blue-700 border-blue-200'
                          }`}>
                          {urgencyLevel === 'CRITICAL' && <AlertCircle size={10} />}
                          {urgencyLevel === 'HIGH' && <Flame size={10} />}
                          {urgencyLevel}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-400 italic">Normal</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-bold border ${isHighPriority && !urgencyLevel ? 'bg-red-600 text-white border-red-700 shadow-sm' : ''} ${p.priority_status === 'U' ? 'bg-red-100 text-red-800 border-red-200' :
                        p.priority_status === 'B' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                          p.priority_status === 'F' ? 'bg-green-100 text-green-800 border-green-200' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {p.priority_status || p.status}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-center flex justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button onClick={(e) => handleEditClick(e, p)} className="text-blue-500 hover:bg-blue-100 p-1.5 rounded"><Edit size={16} /></button>
                      <button onClick={(e) => confirmDeleteClick(e, p)} className="text-red-500 hover:bg-red-100 p-1.5 rounded"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ Pagination Controls */}
      {entriesPerPage !== 'all' && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <ChevronLeft size={16} /> Previous
          </button>

          <div className="flex gap-1">
            {[...Array(totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1.5 border rounded ${currentPage === pageNum
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                return <span key={pageNum} className="px-2">...</span>;
              }
              return null;
            })}
          </div>

          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-1/3 p-6 relative">
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              {isEditing ? <Edit className="text-yellow-500" /> : <Plus className="text-teal-600" />}
              {isEditing ? 'Edit Patient Details' : 'New Patient Registration'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700">Full Name</label><input type="text" name="full_name" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.full_name} onChange={handleInputChange} /></div><div><label className="block text-sm font-medium text-gray-700">Status</label><select name="status" className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-white" value={formData.status} onChange={handleInputChange}><option value="F">Follow Up (F)</option><option value="B">Billing Issue (B)</option><option value="U">Urgent (U)</option><option value="Active">Active</option><option value="Inactive">Inactive</option></select></div></div>
              <div><label className="block text-sm font-medium text-gray-700">Home Address</label><input type="text" name="address" placeholder="Street, City, Zip" className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.address} onChange={handleInputChange} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700">Phone Number</label><input type="text" name="phone_number" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.phone_number} onChange={handleInputChange} /></div>
                <div><label className="block text-sm font-medium text-gray-700">Date of Birth</label><input type="date" name="dob" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.dob} onChange={handleInputChange} /></div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                <input
                  type="checkbox"
                  id="is_priority"
                  name="is_priority"
                  className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  checked={formData.is_priority}
                  onChange={(e) => setFormData({ ...formData, is_priority: e.target.checked })}
                />
                <label htmlFor="is_priority" className="text-sm font-bold text-red-700 cursor-pointer">
                  Mark as High Priority (Always shows on top)
                </label>
              </div>

              <div><label className="block text-sm font-medium text-gray-700">Insurance Provider</label><input type="text" name="insurance_provider" placeholder="e.g. BlueCross" className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.insurance_provider} onChange={handleInputChange} /></div>

              <button type="submit" className={`w-full text-white font-bold py-2 rounded transition mt-4 ${isEditing ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-teal-600 hover:bg-teal-700'}`}>{isEditing ? 'Update Patient' : 'Register Patient'}</button>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && patientToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"><AlertTriangle className="h-6 w-6 text-red-600" /></div>
            <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
            <p className="text-sm text-gray-500 mt-2">Delete <strong>{patientToDelete.full_name}</strong>?</p>
            <div className="mt-6 flex justify-center gap-3"><button onClick={() => setShowDeleteModal(false)} className="border px-4 py-2 rounded">Cancel</button><button onClick={executeDelete} className="bg-red-600 text-white px-4 py-2 rounded">Delete</button></div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PriorityList;